import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import {
  type ActionFunction,
  Link,
  type LoaderFunction,
  Navigate,
  redirect,
  useLoaderData,
  useLocation,
  useNavigate,
  useNavigation,
  useSubmit,
} from "react-router-dom";

import { type PutRequest } from "api";
import {
  getPlatformUserMemberships,
  getUser,
  putPlatformUserMembershipsByPlatformUserMembershipIdCancelMembership,
} from "api/methods";
import { useTranslation } from "@/core/i18n";
import { Button } from "atoms/button/Button";
import { Icon } from "atoms/icon/Icon";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import Close from "atoms/icon/icons/close.svg";
import TopNav from "molecules/navbars/TopNav";
import { queryClient } from "pages/Root";
import { waitObj } from "toolbox/Promise";
import { readFormData } from "toolbox/ReadFormData";

import {
  invalidateMembershipQueries,
  type MembershipCancellationLocationState,
} from "./membershipHelper";

const load = async (platformUserMembershipId: string) => {
  const userMemberships = queryClient.fetchQuery({ ...getPlatformUserMemberships.keyed() });
  const userInfo = queryClient.fetchQuery({ ...getUser.keyed() });

  return waitObj({
    platformUserMembershipId,
    userMemberships,
    userInfo,
  });
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const walletMembershipCancelConfirmLoader: LoaderFunction = ({
  params: { platformUserMembershipId },
}) => {
  if (!platformUserMembershipId) {
    return redirect("..");
  }

  return load(platformUserMembershipId);
};

export const walletMembershipCancelAction: ActionFunction = async ({ request }) => {
  const data =
    await readFormData<
      PutRequest<"/platform-user-memberships/{platformUserMembershipId}/cancel-membership">
    >(request);

  await putPlatformUserMembershipsByPlatformUserMembershipIdCancelMembership(data);

  await invalidateMembershipQueries(queryClient);

  return redirect("/wallet/memberships/manage");
};

export const WalletMembershipCancelConfirmPage = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const {
    platformUserMembershipId,
    userMemberships: initialUserMemberships,
    userInfo,
  } = useLoaderData() as LoaderData;

  const navigate = useNavigate();
  const navigation = useNavigation();
  const submit = useSubmit();

  const { data: userMemberships } = useQuery({
    ...getPlatformUserMemberships.keyed(),
    initialData: initialUserMemberships,
  });
  const membership = userMemberships.find(
    (m) => m.platformUserMembershipId === platformUserMembershipId,
  );

  const { membershipCancellationReasonId } = (location.state ??
    {}) as MembershipCancellationLocationState;

  if (!membershipCancellationReasonId) {
    return <Navigate replace to={`/wallet/memberships/manage/${platformUserMembershipId}`} />;
  }

  const expirationDate = dayjs(membership?.expiration).format("MMM D, YYYY");
  const membershipBenefits =
    membership?.membership.membershipBenefits?.map(({ description }) => description) ?? [];
  // Plans without listed benefits still lose member pricing and rewards.
  const lostItems = membershipBenefits.length
    ? membershipBenefits
    : [
        t("walletMembershipCancelConfirmPage.benefitPricing"),
        t("walletMembershipCancelConfirmPage.benefitMembershipBenefits"),
        t("walletMembershipCancelConfirmPage.benefitTreatmentsAndBonuses"),
      ];

  return (
    <div className="relative flex h-full flex-col bg-white">
      <TopNav
        startIconSVG={ChevronLeft}
        showHeaderItems={false}
        onStartIconClick={() => navigate(-1)}
      >
        <div className="flex justify-center text-sub2">
          {t("walletMembershipCancelConfirmPage.header")}
        </div>
      </TopNav>
      <div className="flex grow flex-col gap-y-6 bg-white px-6 pb-6 pt-3 text-left">
        <div className="flex flex-col gap-y-1">
          <h1 className="text-title">{t("walletMembershipCancelConfirmPage.confirmTitle")}</h1>
          <span className="text-body2 text-secondary">
            {t("walletMembershipCancelConfirmPage.accessUntil", {
              membershipName: membership?.membership.name,
              expirationDate,
            })}
          </span>
        </div>
        <div className="flex flex-col gap-y-3 rounded-xl bg-red/5 p-4">
          <span className="text-bold2 text-primary">
            {t("walletMembershipCancelConfirmPage.youWillLose", { expirationDate })}
          </span>
          <ul className="flex flex-col gap-y-2">
            {lostItems.map((item) => (
              <li key={item} className="flex items-start gap-x-2 text-body2 text-primary">
                <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-red/10 text-red">
                  <Icon svg={Close} color="currentColor" size="x3small" />
                </span>
                {item}
              </li>
            ))}
          </ul>
        </div>
        <div className="flex flex-col gap-y-2 text-body3 text-secondary">
          <span>{t("walletMembershipCancelConfirmPage.unclaimedTreatmentsNotice")}</span>
          <span>
            {t("walletMembershipCancelConfirmPage.undoCancellationNotice", { expirationDate })}
          </span>
          <span>
            {t("walletMembershipCancelConfirmPage.confirmationEmailPrefix")}{" "}
            <span className="text-bold2 text-primary">{userInfo.email}</span>{" "}
            <Link to="/account/details" className="text-bold2 text-brand-color">
              {t("walletMembershipCancelConfirmPage.changeEmailLink")}
            </Link>
          </span>
        </div>
        <div className="grow" />
        <div className="flex flex-col gap-y-3">
          <Button
            fullWidth
            style="dangerSolid"
            disabled={navigation.state !== "idle"}
            onClick={() => {
              submit(
                {
                  platformUserMembershipId,
                  membershipCancellationReasonId,
                },
                { method: "put" },
              );
            }}
          >
            {t("walletMembershipCancelConfirmPage.confirmButton")}
          </Button>
          <Button
            fullWidth
            style="secondary"
            onClick={() => navigate("/wallet/memberships/manage")}
          >
            {t("walletMembershipCancelConfirmPage.keepMembershipButton")}
          </Button>
        </div>
      </div>
    </div>
  );
};
